import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './index.css';
import LandingPage from './pages/LandingPage';
import StartScreen from './pages/StartScreen';
import Auth from './pages/Auth';
import LevelSelect from './pages/LevelSelect';
import Gameplay from './pages/Gameplay';
import LevelComplete from './pages/LevelComplete';
import WinScreen from './pages/WinScreen';
import Leaderboard from './pages/Leaderboard';
import Settings from './pages/Settings';
import PaymentSuccess from './pages/PaymentSuccess';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DiagnosticPage from './pages/DiagnosticPage';
import BibleStudyTips from './pages/BibleStudyTips';
import GameStrategies from './pages/GameStrategies';
import ScriptureReferences from './pages/ScriptureReferences';
import AudioPlayer from './components/AudioPlayer';
import { getMe, exchangeSession } from './utils/api';
import { Toaster } from './components/ui/sonner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        const response = await getMe();
        setUser(response.data);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await exchangeSession(sessionId);
          const userData = response.data.user;
          const token = response.data.session_token;
          
          setUser(userData);
          localStorage.setItem('session_token', token);
          // Clear guest mode flag when user logs in via OAuth
          localStorage.removeItem('guest_mode');
          
          // Clear hash
          window.location.hash = '';
          window.location.href = '/level-select';
        } catch (error) {
          console.error('Session exchange failed:', error);
        }
        setLoading(false);
        return;
      }

      // Check existing session
      const token = localStorage.getItem('session_token');
      if (token) {
        try {
          const response = await getMe();
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('session_token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-purple-800">
        <div className="text-white text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <AudioPlayer />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/start" element={<StartScreen user={user} refreshUser={refreshUser} />} />
          <Route path="/auth" element={<Auth setUser={setUser} />} />
          <Route path="/level-select" element={<LevelSelect user={user} refreshUser={refreshUser} />} />
          <Route path="/gameplay/:level" element={<Gameplay user={user} />} />
          <Route path="/level-complete" element={<LevelComplete user={user} refreshUser={refreshUser} />} />
          <Route path="/win" element={<WinScreen />} />
          <Route path="/leaderboard" element={<Leaderboard user={user} />} />
          <Route path="/settings" element={<Settings user={user} setUser={setUser} refreshUser={refreshUser} />} />
          <Route path="/payment-success" element={<PaymentSuccess user={user} setUser={setUser} />} />
          <Route path="/admin" element={<AdminDashboard user={user} />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/diagnostic" element={<DiagnosticPage />} />
          <Route path="/bible-study-tips" element={<BibleStudyTips />} />
          <Route path="/game-strategies" element={<GameStrategies />} />
          <Route path="/scripture-references" element={<ScriptureReferences />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <Toaster />
      </div>
    </BrowserRouter>
  );
}

export default App;
